import React, { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { RootState, AppDispatch } from '@/store'
import { logoutUser } from '@/store/slices/authSlice'
import { createConversation } from '@/store/slices/messagesSlice'
import { toggleUserLike, fetchUserLikeStatus, selectLikeStatus } from '@/store/slices/likesSlice'
import { User } from '@/types/auth'
import { Gig } from '@/types/gig'
import { SuccessStory } from '@/types/successStory'
import { profileApi, Review } from '@/services/api/profile'
import { gigApi } from '@/services/api/gigs'
import { verificationService } from '@/services/verificationService'
import { successStoriesApi } from '@/services/api/successStories'
import {
    FiUser,
    FiStar,
    FiMapPin,
    FiCalendar,
    FiMail,
    FiPhone,
    FiGlobe,
    FiCheckCircle,
    FiEdit3,
    FiMessageCircle,
    FiBriefcase,
    FiHeart,
    FiSettings,
    FiLogOut,
    FiBook,
    FiEdit2,
    FiExternalLink,
    FiClock,
    FiAlertCircle,
    FiShield,
    FiTrendingUp
} from 'react-icons/fi'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import GigCard from '@/components/gigs/GigCard'
import Avatar from '@/components/ui/Avatar'
import { SubscriptionBadge } from '@/components/subscription/SubscriptionBadge'
import { useAppSelector } from '@/store/hooks'
import { formatDate, truncateText } from '@/utils/formatters'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { CurrencyDisplay } from '@/components/currency'
import { useCurrency } from '@/contexts/CurrencyContext'
import { Accordion, AccordionItem } from '@/components/ui/Accordion'

// Profile Action Buttons Component
interface ProfileActionButtonsProps {
    targetUserId: string
    targetUserName: string
}

const ProfileActionButtons: React.FC<ProfileActionButtonsProps> = ({
    targetUserId,
    targetUserName
}) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { showSuccess, showError } = useSnackbarContext()
    const likeStatus = useSelector(selectLikeStatus('user', targetUserId))

    useEffect(() => {
        dispatch(fetchUserLikeStatus(targetUserId))
    }, [dispatch, targetUserId])

    const handleMessage = async () => {
        try {
            const result = await dispatch(createConversation({
                receiverId: targetUserId
            }))

            if (createConversation.fulfilled.match(result)) {
                navigate('/messages')
                showSuccess(`Ready to chat with ${targetUserName}`)
            }
        } catch (error) {
            showError('Failed to start conversation')
        }
    }

    const handleLike = async () => {
        try {
            await dispatch(toggleUserLike(targetUserId))
            showSuccess(likeStatus.liked ? 'Profile unliked' : 'Profile liked')
        } catch (error) {
            showError('Failed to update like status')
        }
    }

    return (
        <div className="flex gap-2 sm:gap-3 flex-col sm:flex-row">
            <button
                onClick={handleMessage}
                className="inline-flex items-center justify-center sm:justify-start px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm sm:text-base whitespace-nowrap"
            >
                <FiMessageCircle className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                Message
            </button>
            <button
                onClick={handleLike}
                className={`inline-flex items-center justify-center sm:justify-start px-3 sm:px-4 py-2 rounded-lg transition-colors text-sm sm:text-base ${likeStatus.liked
                    ? 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/30'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
            >
                <FiHeart className={`w-4 h-4 sm:w-5 sm:h-5 ${likeStatus.liked ? 'fill-current' : ''}`} />
                {likeStatus.likesCount > 0 && (
                    <span className="ml-1 text-xs sm:text-sm">{likeStatus.likesCount}</span>
                )}
            </button>
        </div>
    )
}

interface InfoItemProps {
    label: string
    value?: React.ReactNode
    highlight?: boolean
}

const InfoItem: React.FC<InfoItemProps> = ({ label, value, highlight }) => {
    if (value === undefined || value === null || value === '') {
        return null
    }

    return (
        <div className="flex flex-col">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {label}
            </span>
            <span
                className={`text-sm sm:text-base font-semibold ${highlight
                    ? 'text-primary-600 dark:text-primary-300'
                    : 'text-gray-900 dark:text-white'
                    }`}
            >
                {value}
            </span>
        </div>
    )
}

const ProfilePage: React.FC = () => {
    const { id } = useParams<{ id?: string }>()
    const navigate = useNavigate()
    const dispatch = useDispatch<AppDispatch>()
    const { user: currentUser, isAuthenticated } = useSelector((state: RootState) => state.auth)
    const userForCards = useAppSelector(state => state.auth.user)
    const { showSuccess, showError, showInfo } = useSnackbarContext()

    const [profileUser, setProfileUser] = useState<User | null>(null)
    const [userGigs, setUserGigs] = useState<Gig[]>([])
    const [reviews, setReviews] = useState<Review[]>([])
    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState<'overview' | 'gigs' | 'reviews'>('overview')
    const [isVerified, setIsVerified] = useState(false)
    const [favoriteGigs, setFavoriteGigs] = useState<Set<string>>(new Set())
    const [successStory, setSuccessStory] = useState<SuccessStory | null>(null)
    const [storyLoading, setStoryLoading] = useState(true)

    const isOwnProfile = !id && isAuthenticated
    const isPublicProfile = !!id

    const formatLabel = (value: string) => value
        .split(/[\s_]/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ')

    const formatNumber = (value?: number) => value !== undefined ? value.toLocaleString() : '--'
    const formatMinutes = (value?: number) => value !== undefined ? `${value} min` : '--'
    const formatPercentage = (value?: number) => value !== undefined ? `${Math.round(value * 100)}%` : undefined
    const formatBoolean = (value?: boolean, affirmative: string = 'Enabled', negative: string = 'Disabled') => value !== undefined ? (value ? affirmative : negative) : undefined
    const formatPercentValue = (value?: number) => value !== undefined ? `${value}%` : undefined
    const formatLimitString = (current?: number, limit?: number, unlimited?: boolean) => {
        if (unlimited || limit === 0) {
            return 'Unlimited'
        }
        if (limit === undefined) {
            return '--'
        }
        return `${current ?? 0}/${limit}`
    }

    const handleSignOut = async () => {
        try {
            await dispatch(logoutUser()).unwrap()
            navigate('/')
        } catch (error) {
            console.error('Sign out error:', error)
        }
    }

    // Fetch user's favorite gigs on mount
    useEffect(() => {
        const fetchUserFavorites = async () => {
            if (!currentUser) return

            try {
                const response = await gigApi.getFavoriteGigs({ page: 1, limit: 1000 })

                if (response.success) {
                    let gigs: Gig[] = []

                    if (response.data?.gigs) {
                        gigs = response.data.gigs
                    }

                    const favoriteIds = new Set<string>(gigs.map((gig: Gig) => gig._id as string))
                    setFavoriteGigs(favoriteIds)
                } else {
                    setFavoriteGigs(new Set())
                }
            } catch (error) {
                console.error('Failed to fetch favorite gigs:', error)
            }
        }

        fetchUserFavorites()
    }, [currentUser])

    const handleFavoriteToggle = async (gigId: string) => {
        const wasInFavorites = favoriteGigs.has(gigId)

        // Optimistically update UI
        const newFavorites = new Set(favoriteGigs)
        if (wasInFavorites) {
            newFavorites.delete(gigId)
        } else {
            newFavorites.add(gigId)
        }
        setFavoriteGigs(newFavorites)

        try {
            const response = await gigApi.toggleFavorite(gigId)
            // Fixed: Use response.data.success instead of response.success
            if (response.success) {
                if (!wasInFavorites) {
                    showSuccess('Added to favorites')
                } else {
                    showInfo('Removed from favorites')
                }
            }
        } catch (error: any) {
            // Revert optimistic update on error
            setFavoriteGigs(favoriteGigs)
            const message = error.response?.data?.error?.message || 'Failed to toggle favorite'
            showError(message)
        }
    }

    useEffect(() => {
        const fetchProfileData = async () => {
            try {
                setLoading(true)

                let targetUser: User | null = null

                if (isOwnProfile && currentUser) {
                    // Get current user profile
                    const profileResponse = await profileApi.getCurrentProfile()
                    // Handle double-nested data structure
                    // @ts-ignore - Handle nested response: { success: true, data: {
                    targetUser = profileResponse?.data?.data?.user || profileResponse?.data?.user
                } else if (isPublicProfile && id) {
                    // Get public profile by ID
                    const profileResponse = await profileApi.getProfile(id)
                    // Handle double-nested data structure
                    // @ts-ignore - Handle nested response: { success: true, data: {
                    targetUser = profileResponse?.data?.data?.user || profileResponse?.data?.user

                    // Record profile view
                    if (isAuthenticated && currentUser?._id !== id) {
                        try {
                            await profileApi.recordProfileView(id)
                        } catch (error) {
                            console.warn('Failed to record profile view:', error)
                        }
                    }
                } else {
                    throw new Error('Invalid profile access')
                }

                if (!targetUser) {
                    throw new Error('User data not found')
                }

                if (!targetUser._id) {
                    throw new Error('User data missing required ID')
                }

                setProfileUser(targetUser)

                // Fetch user gigs
                try {
                    const gigsResponse = await profileApi.getUserGigs(targetUser._id, { limit: 5 })
                    // Handle double-nested data structure
                    // @ts-ignore - Handle nested response: { success: true, data: {
                    const gigsData = gigsResponse?.data?.data?.gigs || gigsResponse?.data?.gigs
                    setUserGigs(gigsData || [])
                } catch (error) {
                    console.warn('Failed to fetch user gigs:', error)
                }

                // Fetch user reviews
                try {
                    const reviewsResponse = await profileApi.getUserReviews(targetUser._id, { limit: 5 })
                    // Handle double-nested data structure
                    // @ts-ignore - Handle nested response: { success: true, data: {
                    const reviewsData = reviewsResponse?.data?.data?.reviews || reviewsResponse?.data?.reviews
                    setReviews(reviewsData || [])
                } catch (error) {
                    console.warn('Failed to fetch user reviews:', error)
                }

                // Fetch verification status (only for current user profile)
                if (isOwnProfile && currentUser) {
                    try {
                        const verificationResponse = await verificationService.getVerificationStatus()
                        if (verificationResponse.success && verificationResponse.data) {
                            setIsVerified((verificationResponse.data.verification?.overallStatus || verificationResponse.data.status) === 'verified')
                        }
                    } catch (error) {
                        console.warn('Failed to fetch verification status:', error)
                    }
                }

            } catch (error) {
                console.error('Failed to fetch profile data:', error)
                setProfileUser(null)
                if (isPublicProfile) {
                    navigate('/404')
                }
            } finally {
                setLoading(false)
            }
        }

        fetchProfileData()
    }, [id, currentUser, isOwnProfile, isPublicProfile, navigate, isAuthenticated])

    // Load success story for user's own profile
    useEffect(() => {
        const fetchSuccessStory = async () => {
            if (!isOwnProfile || !isAuthenticated) {
                setStoryLoading(false)
                return
            }

            try {
                setStoryLoading(true)
                const response = await successStoriesApi.getUserSuccessStory()
                if (response.data?.story) {
                    setSuccessStory(response.data.story)
                }
            } catch (error) {
                console.error('Failed to load success story:', error)
            } finally {
                setStoryLoading(false)
            }
        }

        fetchSuccessStory()
    }, [isOwnProfile, isAuthenticated])

    if (loading) {
        return (
            <div className="flex items-center justify-center py-16">
                <LoadingSpinner size="xl" />
            </div>
        )
    }

    if (!profileUser) {
        return (
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                        Profile Not Found
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-8">
                        The profile you're looking for doesn't exist.
                    </p>
                    <Link
                        to="/"
                        className="inline-flex items-center px-6 py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors"
                    >
                        Go Home
                    </Link>
                </div>
            </div>
        )
    }

    const fullName = `${profileUser.profile?.firstName} ${profileUser.profile?.lastName}`
    const isEmployer = profileUser.role === 'employer'
    const isWorker = profileUser.role === 'worker'
    const subscriptionTier = profileUser.subscription?.tier
    const identityStatus = profileUser.identityVerification?.status
    const backgroundCheckStatus = profileUser.backgroundCheckStatus
    const riskLevel = profileUser.riskLevel
    const verificationLevelLabel = profileUser.verificationLevel ? formatLabel(profileUser.verificationLevel) : undefined
    const identityStatusLabel = identityStatus ? formatLabel(identityStatus) : undefined
    const backgroundStatusLabel = backgroundCheckStatus ? formatLabel(backgroundCheckStatus) : undefined
    const riskLevelLabel = riskLevel ? formatLabel(riskLevel) : undefined
    const lastActiveLabel = profileUser.activityMetrics?.lastActivityAt ? formatDate(profileUser.activityMetrics.lastActivityAt) : undefined
    const locationLabel = profileUser.profile?.location?.address || profileUser.profile?.location?.city || profileUser.country
    const preferredCategories = profileUser.preferences?.preferredCategories?.filter(Boolean) || []
    const hasSkills = (profileUser.profile?.skills || []).length > 0
    const defaultPaymentMethod = profileUser.paymentMethods?.find((method) => method.isDefault)
    const monthlyGigLimit = profileUser.monthlyAccessLimits?.gigs?.limit
    const monthlyGigCurrent = profileUser.monthlyAccessLimits?.gigs?.current
    const monthlyApplicationLimit = profileUser.monthlyAccessLimits?.applications?.limit
    const monthlyApplicationCurrent = profileUser.monthlyAccessLimits?.applications?.current
    const commissionSettings = profileUser.commissionSettings
    const currencyCode = profileUser.earnings?.currency || 'USD'
    const languagePreference = profileUser.preferences?.language ? profileUser.preferences.language.toUpperCase() : undefined
    const themePreference = profileUser.preferences?.theme ? formatLabel(profileUser.preferences.theme) : undefined
    const jobAlertsLabel = formatBoolean(profileUser.preferences?.jobAlerts)
    const workRemotelyLabel = formatBoolean(profileUser.preferences?.workRemotely, 'Available', 'Not available')
    const travelWillingnessLabel = formatBoolean(profileUser.preferences?.travelWillingness, 'Yes', 'No')
    const distanceRadiusLabel = profileUser.preferences?.distanceRadius !== undefined ? `${profileUser.preferences.distanceRadius} km` : undefined
    const defaultPaymentMask = defaultPaymentMethod?.maskedInfo
    const linkedinProfile = profileUser.businessProfile?.linkedinProfile
    const verificationAccessLimits = profileUser.verificationAccessLimits
    const communicationPreferences = profileUser.communicationPreferences
    const trustSystem = profileUser.trustSystem
    const bankVerificationLabel = formatBoolean(profileUser.bankDetails?.isVerified, 'Verified', 'Pending')
    const subscriptionStatusLabel = profileUser.subscription?.status ? formatLabel(profileUser.subscription.status) : undefined
    const subscriptionAutoRenewLabel = profileUser.subscription?.autoRenew !== undefined ? formatBoolean(profileUser.subscription.autoRenew, 'Enabled', 'Disabled') : undefined
    const subscriptionStartLabel = profileUser.subscription?.startDate ? formatDate(profileUser.subscription.startDate) : undefined
    const monthlyGigUsageLabel = monthlyGigLimit !== undefined ? formatLimitString(monthlyGigCurrent, monthlyGigLimit, monthlyGigLimit === 0) : undefined
    const monthlyApplicationUsageLabel = monthlyApplicationLimit !== undefined ? formatLimitString(monthlyApplicationCurrent, monthlyApplicationLimit, monthlyApplicationLimit === 0) : undefined
    const verificationGigUsageLabel = verificationAccessLimits?.gigs ? formatLimitString(verificationAccessLimits.gigs.current, verificationAccessLimits.gigs.limit, verificationAccessLimits.gigs.unlimited) : undefined
    const verificationApplicationUsageLabel = verificationAccessLimits?.applications ? formatLimitString(verificationAccessLimits.applications.current, verificationAccessLimits.applications.limit, verificationAccessLimits.applications.unlimited) : undefined
    const verificationGigReset = verificationAccessLimits?.gigs?.lastResetAt ? formatDate(verificationAccessLimits.gigs.lastResetAt) : undefined
    const verificationApplicationReset = verificationAccessLimits?.applications?.lastResetAt ? formatDate(verificationAccessLimits.applications.lastResetAt) : undefined
    const verificationOverridesCount = verificationAccessLimits?.overrides?.length ? formatNumber(verificationAccessLimits.overrides.length) : undefined
    const gigUnlimitedLabel = verificationAccessLimits?.gigs?.unlimited !== undefined ? formatBoolean(verificationAccessLimits.gigs.unlimited, 'Yes', 'No') : undefined
    const applicationUnlimitedLabel = verificationAccessLimits?.applications?.unlimited !== undefined ? formatBoolean(verificationAccessLimits.applications.unlimited, 'Yes', 'No') : undefined
    const trustLevelLabel = trustSystem?.level ? formatLabel(trustSystem.level) : undefined
    const completionRateLabel = formatPercentValue(trustSystem?.completionRate)
    const positiveReviewRateLabel = formatPercentValue(trustSystem?.positiveReviewRate)
    const disputeRateLabel = formatPercentValue(trustSystem?.disputeRate)
    const trustScoreLabel = trustSystem?.currentScore !== undefined ? formatNumber(trustSystem.currentScore) : undefined
    const trustBaseScoreLabel = trustSystem?.baseScore !== undefined ? formatNumber(trustSystem.baseScore) : undefined
    const trustAccessLimits = trustSystem?.accessLimits
    const verificationBenefits = profileUser.identityVerification?.benefits
    const hasVerificationBenefits = Boolean(verificationBenefits && (
        verificationBenefits.badgeEnabled ||
        verificationBenefits.premiumAccess ||
        verificationBenefits.prioritySupport ||
        verificationBenefits.higherLimits ||
        verificationBenefits.feeReduction
    ))

    const metricCards: Array<{ label: string; value: string; icon: React.ReactNode }> = [
        {
            label: 'Trust Score',
            value: profileUser.trustScore !== undefined ? `${profileUser.trustScore}/100` : '--',
            icon: <FiShield className="w-5 h-5 text-primary-600 dark:text-primary-400" />
        },
        {
            label: isEmployer ? 'Applications Received' : 'Completed Projects',
            value: formatNumber(isEmployer ? profileUser.activityMetrics?.applicationsReceived : profileUser.activityMetrics?.successfulCompletions),
            icon: <FiBriefcase className="w-5 h-5 text-blue-500 dark:text-blue-300" />
        },
        {
            label: 'Avg. Response Time',
            value: formatMinutes(profileUser.activityMetrics?.averageResponseTime),
            icon: <FiClock className="w-5 h-5 text-amber-500 dark:text-amber-300" />
        },
        {
            label: 'Profile Views',
            value: formatNumber(profileUser.activityMetrics?.profileViews),
            icon: <FiTrendingUp className="w-5 h-5 text-emerald-500 dark:text-emerald-300" />
        }
    ]

    return (
        <>
            <Helmet>
                <title>{isOwnProfile ? 'My Profile' : `${fullName}'s Profile`} - SideGO</title>
                <meta name="description" content={profileUser.profile?.bio ? truncateText(profileUser.profile.bio, 160) : `View ${fullName}'s profile on SideGO`} />
            </Helmet>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
                {/* Profile Header */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-primary-500/5 via-transparent to-transparent dark:from-primary-500/10" />
                    <div className="absolute -top-24 -right-24 w-64 h-64 bg-primary-500/10 dark:bg-primary-500/20 blur-3xl rounded-full" />
                    {isOwnProfile && (
                        <Link
                            to="/dashboard/profile/edit"
                            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                        >
                            <FiSettings className="w-5 h-5" />
                        </Link>
                    )}
                    <div className="relative z-10">
                        <div className="flex flex-col md:flex-row gap-6">
                            <div className="flex flex-col items-center md:items-start gap-4">
                                <div className="relative">
                                    <Avatar
                                        avatarId={profileUser.profile?.avatar}
                                        alt={fullName}
                                        size="xl"
                                        className="w-32 h-32"
                                        showBorder={true}
                                    />
                                    {isVerified && (
                                        <div className="absolute -bottom-2 -right-2 bg-green-500 rounded-full p-2">
                                            <FiCheckCircle className="w-4 h-4 text-white" />
                                        </div>
                                    )}
                                </div>

                                <div className="w-full md:w-auto flex flex-col gap-2">
                                    {isOwnProfile && (
                                        <>
                                            <Link
                                                to="/dashboard/profile/edit"
                                                className="inline-flex items-center justify-center md:justify-start px-3 sm:px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm sm:text-base"
                                            >
                                                <FiEdit3 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                                Edit Profile
                                            </Link>
                                            <button
                                                onClick={handleSignOut}
                                                className="inline-flex items-center justify-center md:justify-start px-3 sm:px-4 py-2 bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 rounded-lg hover:bg-red-200 dark:hover:bg-red-800 transition-colors text-sm sm:text-base"
                                            >
                                                <FiLogOut className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                                                Sign Out
                                            </button>
                                        </>
                                    )}

                                    {isPublicProfile && isAuthenticated && currentUser?._id !== profileUser._id && (
                                        <ProfileActionButtons
                                            targetUserId={profileUser._id}
                                            targetUserName={fullName}
                                        />
                                    )}
                                </div>
                            </div>

                            <div className="flex-1 flex flex-col gap-4">
                                <div className="flex flex-col gap-3">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-semibold uppercase tracking-wide">
                                            <FiUser className="w-4 h-4" />
                                            {formatLabel(profileUser.role)}
                                        </span>

                                        {subscriptionTier && isOwnProfile && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-semibold uppercase tracking-wide">
                                                <FiBriefcase className="w-4 h-4" />
                                                {formatLabel(subscriptionTier)} Plan
                                            </span>
                                        )}

                                        {identityStatusLabel && isOwnProfile && (
                                            <span
                                                className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${identityStatus === 'verified'
                                                    ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-200'
                                                    : identityStatus === 'pending'
                                                        ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-200'
                                                        : identityStatus === 'rejected'
                                                            ? 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
                                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                                    }`}
                                            >
                                                <FiShield className="w-4 h-4" />
                                                {identityStatusLabel}
                                            </span>
                                        )}

                                        {backgroundStatusLabel && backgroundCheckStatus !== 'not_required' && isOwnProfile && (
                                            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-xs font-semibold uppercase tracking-wide">
                                                <FiShield className="w-4 h-4" />
                                                {backgroundStatusLabel}
                                            </span>
                                        )}

                                        {riskLevelLabel && isOwnProfile && (
                                            <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${riskLevel === 'low'
                                                ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-200'
                                                : riskLevel === 'medium'
                                                    ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-200'
                                                    : 'bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200'
                                                }`}>
                                                <FiAlertCircle className="w-4 h-4" />
                                                {riskLevelLabel}
                                            </span>
                                        )}
                                    </div>

                                    <div>
                                        <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2">
                                            {fullName}
                                        </h1>
                                        <div className="flex flex-wrap items-center gap-3 text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                                            {locationLabel && (
                                                <span className="inline-flex items-center gap-1">
                                                    <FiMapPin className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    {locationLabel}
                                                </span>
                                            )}
                                            <span className="inline-flex items-center gap-1">
                                                <FiCalendar className="w-3 h-3 sm:w-4 sm:h-4" />
                                                Joined {formatDate(profileUser.createdAt)}
                                            </span>
                                            {lastActiveLabel && (
                                                <span className="inline-flex items-center gap-1">
                                                    <FiClock className="w-3 h-3 sm:w-4 sm:h-4" />
                                                    Last active {lastActiveLabel}
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {isEmployer && (profileUser.businessProfile?.companyName || profileUser.profile?.company) && (
                                        <div className="text-sm sm:text-base text-gray-700 dark:text-gray-300">
                                            <span className="font-semibold text-gray-900 dark:text-white">Company:</span>{' '}
                                            {profileUser.businessProfile?.companyName || profileUser.profile?.company}
                                        </div>
                                    )}

                                    {profileUser.ratings && profileUser.ratings.count > 0 && (
                                        <div className="flex items-center gap-2">
                                            <div className="flex items-center">
                                                {[...Array(5)].map((_, i) => (
                                                    <FiStar
                                                        key={i}
                                                        className={`w-4 h-4 sm:w-5 sm:h-5 ${i < Math.floor(profileUser.ratings?.average || 0)
                                                            ? 'text-yellow-500 fill-current'
                                                            : 'text-gray-300 dark:text-gray-600'
                                                            }`}
                                                    />
                                                ))}
                                            </div>
                                            <span className="font-semibold text-gray-900 dark:text-white">
                                                {profileUser.ratings.average.toFixed(1)}
                                            </span>
                                            <span className="text-gray-600 dark:text-gray-400">
                                                ({profileUser.ratings.count} reviews)
                                            </span>
                                        </div>
                                    )}

                                    <div className="flex flex-wrap items-center gap-3">
                                        <SubscriptionBadge user={profileUser} compact={false} showBg={true} />
                                    </div>
                                </div>

                                {profileUser.profile?.bio && (
                                    <p className="text-sm sm:text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                                        {profileUser.profile.bio}
                                    </p>
                                )}

                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-400">
                                    {(isOwnProfile || isPublicProfile) && profileUser.profile?.phone && (
                                        <div className="inline-flex items-center gap-2">
                                            <FiPhone className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                            {profileUser.profile.phone}
                                        </div>
                                    )}

                                    {isOwnProfile && (
                                        <div className="inline-flex items-center gap-2">
                                            <FiMail className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                            <span className="truncate">{profileUser.email}</span>
                                        </div>
                                    )}

                                    {profileUser.businessProfile?.website && (
                                        <a
                                            href={profileUser.businessProfile.website}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 hover:text-primary-600 dark:hover:text-primary-400 transition-colors truncate"
                                        >
                                            <FiGlobe className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                                            <span className="truncate">{profileUser.businessProfile.website}</span>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>

                        {isWorker && isOwnProfile && (
                            <div className="mt-6">
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-3">
                                    Earnings Snapshot
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
                                    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm p-4 flex flex-col gap-1">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Total Earned
                                        </span>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                            <CurrencyDisplay amount={profileUser.earnings?.totalEarned || 0} currency={currencyCode} />
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm p-4 flex flex-col gap-1">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Available Balance
                                        </span>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                            <CurrencyDisplay amount={profileUser.earnings?.availableBalance || 0} currency={currencyCode} />
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm p-4 flex flex-col gap-1">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Pending Payouts
                                        </span>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                            <CurrencyDisplay amount={profileUser.earnings?.pendingPayouts || 0} currency={currencyCode} />
                                        </div>
                                    </div>
                                    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white/80 dark:bg-gray-800/90 backdrop-blur-sm p-4 flex flex-col gap-1">
                                        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                            Total Withdrawn
                                        </span>
                                        <div className="text-lg font-semibold text-gray-900 dark:text-white">
                                            <CurrencyDisplay amount={profileUser.earnings?.totalWithdrawn || 0} currency={currencyCode} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className={`${isWorker ? 'mt-4' : 'mt-6'}`}>
                            <Accordion>
                                <AccordionItem 
                                    title="Performance Highlights" 
                                    defaultOpen={true}
                                    icon={<FiTrendingUp className="w-5 h-5" />}
                                >
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        {metricCards.map((card) => (
                                            <div
                                                key={card.label}
                                                className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 p-4 flex flex-col gap-2"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 rounded-lg bg-white dark:bg-gray-800">
                                                        {card.icon}
                                                    </div>
                                                    <div>
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                            {card.label}
                                                        </p>
                                                        <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                                                            {card.value}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </AccordionItem>
                            </Accordion>
                        </div>
                    </div>
                </div>



                {/* Tabs */}
                <div className="mb-6 sm:mb-8">
                    <div className="border-b border-gray-200 dark:border-gray-700">
                        <nav className="-mb-px flex space-x-4 sm:space-x-6 md:space-x-8 overflow-x-auto scrollbar-hide">
                            <button
                                onClick={() => setActiveTab('overview')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm md:text-base whitespace-nowrap ${activeTab === 'overview'
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                Overview
                            </button>
                            <button
                                onClick={() => setActiveTab('gigs')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm md:text-base whitespace-nowrap ${activeTab === 'gigs'
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                {profileUser.role === 'worker' ? 'Work History' : 'Posted Gigs'} ({userGigs.length})
                            </button>
                            <button
                                onClick={() => setActiveTab('reviews')}
                                className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm md:text-base whitespace-nowrap ${activeTab === 'reviews'
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                                    }`}
                            >
                                Reviews ({reviews.length})
                            </button>
                        </nav>
                    </div>
                </div>

                {/* Tab Content */}
                <div>
                    {activeTab === 'overview' && (
                        <div className="space-y-6 sm:space-y-8">
                            {isOwnProfile && (
                                <Accordion>
                                    <AccordionItem 
                                        title="Account Snapshot" 
                                        defaultOpen={true}
                                        icon={<FiUser className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                            <InfoItem label="Account Status" value={profileUser.isActive ? 'Active' : 'Inactive'} highlight={profileUser.isActive} />
                                            <InfoItem label="Subscription Tier" value={subscriptionTier ? formatLabel(subscriptionTier) : undefined} />
                                            <InfoItem label="Subscription Status" value={subscriptionStatusLabel} />
                                            <InfoItem label="Subscription Started" value={subscriptionStartLabel} />
                                            <InfoItem label="Auto Renew" value={subscriptionAutoRenewLabel} highlight={profileUser.subscription?.autoRenew} />
                                            <InfoItem label="Verification Level" value={verificationLevelLabel} />
                                            <InfoItem label="Identity Status" value={identityStatusLabel} highlight={identityStatus === 'verified'} />
                                            <InfoItem label="Background Check" value={backgroundStatusLabel} />
                                            <InfoItem label="Email Verified" value={formatBoolean(profileUser.emailVerified, 'Verified', 'Pending')} highlight={profileUser.emailVerified} />
                                            <InfoItem label="Phone Verified" value={formatBoolean(profileUser.phoneVerified, 'Verified', 'Pending')} highlight={profileUser.phoneVerified} />
                                            <InfoItem label="Bank Details" value={bankVerificationLabel} highlight={profileUser.bankDetails?.isVerified} />
                                            <InfoItem label="Two-Factor" value={formatBoolean(profileUser.twoFactorEnabled)} highlight={profileUser.twoFactorEnabled} />
                                            <InfoItem label="Last Login" value={profileUser.lastLogin ? formatDate(profileUser.lastLogin) : undefined} />
                                            <InfoItem label="Risk Level" value={riskLevelLabel} highlight={riskLevel === 'low'} />
                                        </div>
                                    </AccordionItem>

                                    <AccordionItem 
                                        title="Contact & Preferences" 
                                        icon={<FiSettings className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                            <InfoItem label="Email" value={profileUser.email} />
                                            <InfoItem label="Phone" value={profileUser.profile?.phone} />
                                            <InfoItem label="Timezone" value={profileUser.preferences?.timezone} />
                                            <InfoItem label="Language" value={languagePreference} />
                                            <InfoItem label="Theme" value={themePreference} />
                                            <InfoItem label="Preferred Currency" value={profileUser.preferences?.currency} />
                                            <InfoItem label="Job Alerts" value={jobAlertsLabel} highlight={profileUser.preferences?.jobAlerts} />
                                            <InfoItem label="Default Payout" value={defaultPaymentMask} />
                                        </div>
                                    </AccordionItem>

                                    <AccordionItem 
                                        title={isEmployer ? 'Company Overview' : 'Professional Focus'}
                                        icon={<FiBriefcase className="w-5 h-5" />}
                                    >
                                        {isEmployer ? (
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                            <InfoItem label="Company" value={profileUser.businessProfile?.companyName || profileUser.profile?.company} />
                                            <InfoItem label="Industry" value={profileUser.businessProfile?.industry} />
                                            <InfoItem label="Company Size" value={profileUser.businessProfile?.companySize} />
                                            {linkedinProfile && (
                                                <InfoItem
                                                    label="LinkedIn"
                                                    value={(
                                                        <a
                                                            href={linkedinProfile}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-primary-600 dark:text-primary-400 hover:underline"
                                                        >
                                                            View Profile
                                                        </a>
                                                    )}
                                                />
                                            )}
                                            {isOwnProfile && (
                                                <>
                                                    <InfoItem label="Platform Commission" value={formatPercentage(commissionSettings?.platformCommission)} />
                                                    <InfoItem label="Buyer Protection" value={formatPercentage(commissionSettings?.buyerProtectionFee)} />
                                                    <InfoItem
                                                        label="Gig Limit"
                                                        value={monthlyGigUsageLabel}
                                                    />
                                                    <InfoItem
                                                        label="Application Limit"
                                                        value={monthlyApplicationUsageLabel}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="space-y-4">
                                            {hasSkills && (
                                                <div>
                                                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                                                        Core Skills
                                                    </p>
                                                    <div className="flex flex-wrap gap-2">
                                                        {profileUser.profile?.skills?.map((skill, index) => (
                                                            <span
                                                                key={index}
                                                                className="px-2 sm:px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300 text-xs sm:text-sm font-medium rounded-full"
                                                            >
                                                                {skill}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                <InfoItem label="Remote Availability" value={workRemotelyLabel} highlight={profileUser.preferences?.workRemotely} />
                                                <InfoItem label="Travel Willingness" value={travelWillingnessLabel} highlight={profileUser.preferences?.travelWillingness} />
                                                <InfoItem label="Service Radius" value={distanceRadiusLabel} />
                                                {preferredCategories.length > 0 && (
                                                    <div className="lg:col-span-3">
                                                        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">
                                                            Preferred Categories
                                                        </p>
                                                        <div className="flex flex-wrap gap-2">
                                                            {preferredCategories.map((category) => (
                                                                <span
                                                                    key={category}
                                                                    className="px-2 sm:px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-xs sm:text-sm font-medium rounded-full"
                                                                >
                                                                    {category}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        )}
                                    </AccordionItem>
                                </Accordion>
                            )}

                            {isOwnProfile && (
                                <Accordion>
                                    <AccordionItem 
                                        title="Access & Limits" 
                                        icon={<FiShield className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                <InfoItem label="Monthly Gig Posts" value={monthlyGigUsageLabel} />
                                                <InfoItem label="Monthly Applications" value={monthlyApplicationUsageLabel} />
                                                <InfoItem label="Verification Gig Access" value={verificationGigUsageLabel} />
                                                <InfoItem label="Verification Application Access" value={verificationApplicationUsageLabel} />
                                                <InfoItem label="Unlimited Gig Posts" value={gigUnlimitedLabel} highlight={verificationAccessLimits?.gigs?.unlimited} />
                                                <InfoItem label="Unlimited Applications" value={applicationUnlimitedLabel} highlight={verificationAccessLimits?.applications?.unlimited} />
                                                <InfoItem label="Gig Reset" value={verificationGigReset} />
                                                <InfoItem label="Application Reset" value={verificationApplicationReset} />
                                                <InfoItem label="Access Overrides" value={verificationOverridesCount} />
                                        </div>
                                    </AccordionItem>

                                    <AccordionItem 
                                        title="Trust & Safety" 
                                        icon={<FiShield className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                <InfoItem label="Trust Level" value={trustLevelLabel} />
                                                <InfoItem label="Trust Score" value={trustScoreLabel} />
                                                <InfoItem label="Base Score" value={trustBaseScoreLabel} />
                                                <InfoItem label="Completion Rate" value={completionRateLabel} />
                                                <InfoItem label="Positive Review Rate" value={positiveReviewRateLabel} />
                                                <InfoItem label="Dispute Rate" value={disputeRateLabel} />
                                                <InfoItem
                                                    label="Max Gig Value"
                                                    value={trustAccessLimits?.maxGigValue !== undefined ? (
                                                        <CurrencyDisplay amount={trustAccessLimits.maxGigValue} currency={currencyCode} compact />
                                                    ) : undefined}
                                                />
                                                <InfoItem
                                                    label="Max Application Value"
                                                    value={trustAccessLimits?.maxApplicationValue !== undefined ? (
                                                        <CurrencyDisplay amount={trustAccessLimits.maxApplicationValue} currency={currencyCode} compact />
                                                    ) : undefined}
                                                />
                                                <InfoItem label="Premium Categories" value={trustAccessLimits?.canAccessPremiumCategories !== undefined ? formatBoolean(trustAccessLimits.canAccessPremiumCategories, 'Enabled', 'Restricted') : undefined} highlight={trustAccessLimits?.canAccessPremiumCategories} />
                                                <InfoItem label="Unlimited Posting" value={trustAccessLimits?.canPostUnlimitedGigs !== undefined ? formatBoolean(trustAccessLimits.canPostUnlimitedGigs, 'Enabled', 'Limited') : undefined} highlight={trustAccessLimits?.canPostUnlimitedGigs} />
                                                <InfoItem label="Score Updated" value={trustSystem?.lastScoreUpdate ? formatDate(trustSystem.lastScoreUpdate) : undefined} />
                                                <InfoItem label="Time on Platform" value={trustSystem?.timeOnPlatform !== undefined ? `${formatNumber(trustSystem.timeOnPlatform)} days` : undefined} />
                                        </div>
                                    </AccordionItem>

                                    <AccordionItem 
                                        title="Communication Preferences" 
                                        icon={<FiMail className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                <InfoItem label="Email Notifications" value={formatBoolean(communicationPreferences?.emailNotifications)} highlight={communicationPreferences?.emailNotifications} />
                                                <InfoItem label="Email Application Updates" value={formatBoolean(communicationPreferences?.emailApplicationUpdates)} highlight={communicationPreferences?.emailApplicationUpdates} />
                                                <InfoItem label="Email Security Alerts" value={formatBoolean(communicationPreferences?.emailSecurityAlerts)} highlight={communicationPreferences?.emailSecurityAlerts} />
                                                <InfoItem label="Email Marketing" value={formatBoolean(communicationPreferences?.emailMarketingUpdates, 'Enabled', 'Disabled')} />
                                                <InfoItem label="SMS Notifications" value={formatBoolean(communicationPreferences?.smsNotifications)} highlight={communicationPreferences?.smsNotifications} />
                                                <InfoItem label="SMS Security Alerts" value={formatBoolean(communicationPreferences?.smsSecurityAlerts)} highlight={communicationPreferences?.smsSecurityAlerts} />
                                                <InfoItem label="Push Notifications" value={formatBoolean(communicationPreferences?.pushNotifications)} highlight={communicationPreferences?.pushNotifications} />
                                                <InfoItem label="Unsubscribe All" value={formatBoolean(communicationPreferences?.unsubscribeAll, 'Enabled', 'Disabled')} />
                                        </div>
                                    </AccordionItem>

                                    {hasVerificationBenefits && (
                                        <AccordionItem 
                                            title="Verification Benefits" 
                                            icon={<FiCheckCircle className="w-5 h-5" />}
                                        >
                                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                                    <InfoItem label="Verification Badge" value={formatBoolean(verificationBenefits?.badgeEnabled, 'Enabled', 'Unavailable')} highlight={verificationBenefits?.badgeEnabled} />
                                                    <InfoItem label="Fee Reduction" value={formatPercentValue(verificationBenefits?.feeReduction)} />
                                                    <InfoItem label="Premium Access" value={formatBoolean(verificationBenefits?.premiumAccess, 'Available', 'Locked')} highlight={verificationBenefits?.premiumAccess} />
                                                    <InfoItem label="Priority Support" value={formatBoolean(verificationBenefits?.prioritySupport, 'Available', 'Standard')} highlight={verificationBenefits?.prioritySupport} />
                                                    <InfoItem label="Higher Limits" value={formatBoolean(verificationBenefits?.higherLimits, 'Enabled', 'Standard')} highlight={verificationBenefits?.higherLimits} />
                                            </div>
                                        </AccordionItem>
                                    )}
                                </Accordion>
                            )}

                            {isEmployer && isOwnProfile && (
                                <Accordion>
                                    <AccordionItem 
                                        title="Recruitment Insights" 
                                        icon={<FiTrendingUp className="w-5 h-5" />}
                                    >
                                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                        <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-4 flex flex-col gap-1">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Applications Received
                                            </span>
                                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {formatNumber(profileUser.activityMetrics?.applicationsReceived)}
                                            </span>
                                        </div>
                                        <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-4 flex flex-col gap-1">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Gig Views
                                            </span>
                                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {formatNumber(profileUser.activityMetrics?.gigViews)}
                                            </span>
                                        </div>
                                        <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-4 flex flex-col gap-1">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Successful Completions
                                            </span>
                                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {formatNumber(profileUser.activityMetrics?.successfulCompletions)}
                                            </span>
                                        </div>
                                        <div className="rounded-lg border border-gray-100 dark:border-gray-700 p-4 flex flex-col gap-1">
                                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                Disputes Raised
                                            </span>
                                            <span className="text-lg font-semibold text-gray-900 dark:text-white">
                                                {formatNumber(profileUser.activityMetrics?.disputesRaised)}
                                            </span>
                                        </div>
                                        </div>
                                    </AccordionItem>
                                </Accordion>
                            )}

                            <Accordion>
                                <AccordionItem 
                                    title="Recent Activity" 
                                    icon={<FiBriefcase className="w-5 h-5" />}
                                >
                                    <div className="space-y-3 sm:space-y-4">
                                        {userGigs.slice(0, 3).map((gig) => (
                                            <div key={gig._id} className="flex items-start gap-3 p-3 sm:p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                                                <div className="flex-shrink-0">
                                                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-primary-100 dark:bg-primary-900 rounded-lg flex items-center justify-center">
                                                        <FiBriefcase className="w-4 h-4 sm:w-5 sm:h-5 text-primary-600 dark:text-primary-400" />
                                                    </div>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white line-clamp-2">
                                                        {gig.title}
                                                    </p>
                                                    <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-1">
                                                        <CurrencyDisplay
                                                            amount={gig.payment.amount}
                                                            currency={gig.payment.currency}
                                                            compact
                                                        />
                                                        <span>•</span>
                                                        <span>{formatDate(gig.createdAt)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                        {userGigs.length === 0 && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                No recent activity recorded.
                                            </p>
                                        )}
                                    </div>
                                </AccordionItem>

                                <AccordionItem 
                                    title="Latest Reviews" 
                                    icon={<FiStar className="w-5 h-5" />}
                                >
                                    <div className="space-y-4">
                                        {reviews.slice(0, 2).map((review) => (
                                            <div key={review._id} className="border-b border-gray-200 dark:border-gray-700 last:border-b-0 pb-4 last:pb-0">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <div className="flex">
                                                        {[...Array(5)].map((_, i) => (
                                                            <FiStar
                                                                key={i}
                                                                className={`w-3 h-3 sm:w-4 sm:h-4 ${i < review.rating
                                                                    ? 'text-yellow-500 fill-current'
                                                                    : 'text-gray-300 dark:text-gray-600'
                                                                    }`}
                                                            />
                                                        ))}
                                                    </div>
                                                    <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                                        {formatDate(review.date)}
                                                    </span>
                                                </div>
                                                <p className="text-xs sm:text-sm text-gray-700 dark:text-gray-300 mb-2">
                                                    {truncateText(review.comment, 120)}
                                                </p>
                                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                                    by {review.reviewer.name} for "{review.gigTitle}"
                                                </p>
                                            </div>
                                        ))}
                                        {reviews.length === 0 && (
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                No reviews available yet.
                                            </p>
                                        )}
                                    </div>
                                </AccordionItem>
                            </Accordion>

                            {isOwnProfile && !storyLoading && (
                                <Accordion>
                                    <AccordionItem 
                                        title="Your Success Story" 
                                        icon={<FiBook className="w-5 h-5" />}
                                    >
                                        {successStory ? (
                                        <div className="space-y-4">
                                            <div>
                                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                    Title
                                                </p>
                                                <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                                                    {successStory.title}
                                                </p>
                                            </div>
                                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-2">
                                                <div>
                                                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                        Status
                                                    </p>
                                                    <div className="flex items-center">
                                                        {successStory.status === 'pending' && (
                                                            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 text-xs font-medium rounded-full">
                                                                <FiClock className="w-3 h-3" />
                                                                Pending Review
                                                            </span>
                                                        )}
                                                        {successStory.status === 'approved' && (
                                                            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium rounded-full">
                                                                <FiCheckCircle className="w-3 h-3" />
                                                                Published
                                                            </span>
                                                        )}
                                                        {successStory.status === 'rejected' && (
                                                            <span className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 text-xs font-medium rounded-full">
                                                                <FiAlertCircle className="w-3 h-3" />
                                                                Rejected
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                {successStory.status === 'approved' && (
                                                    <div>
                                                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-1">
                                                            Views
                                                        </p>
                                                        <p className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                                                            {successStory.views}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col sm:flex-row gap-2 pt-2">
                                                <button
                                                    onClick={() => navigate('/dashboard/success-story')}
                                                    className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                                                >
                                                    <FiExternalLink className="w-4 h-4" />
                                                    View
                                                </button>
                                                {successStory.status !== 'archived' && (
                                                    <button
                                                        onClick={() => navigate(`/success-stories/${successStory._id}/edit`)}
                                                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                                                    >
                                                        <FiEdit2 className="w-4 h-4" />
                                                        Edit
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center py-6">
                                            <FiBook className="w-8 h-8 sm:w-10 sm:h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-4">
                                                You haven't submitted a success story yet.
                                            </p>
                                            <button
                                                onClick={() => navigate('/success-stories/submit')}
                                                className="px-3 sm:px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs sm:text-sm font-medium rounded-lg transition-colors"
                                            >
                                                Submit Your Story
                                            </button>
                                        </div>
                                        )}
                                    </AccordionItem>
                                </Accordion>
                            )}
                        </div>
                    )}

                    {activeTab === 'gigs' && (
                        <div>
                            {userGigs.length > 0 ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6">
                                    {userGigs.map((gig) => (
                                        <GigCard
                                            key={gig._id}
                                            gig={gig}
                                            onFavorite={handleFavoriteToggle}
                                            isFavorited={favoriteGigs.has(gig._id)}
                                            currentUser={userForCards}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 sm:py-16">
                                    <FiBriefcase className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-gray-900 dark:text-white mb-2">
                                        No gigs yet
                                    </h3>
                                    <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">
                                        {profileUser.role === 'worker'
                                            ? 'No completed work history to show.'
                                            : 'No gigs posted yet.'
                                        }
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <div>
                            {reviews.length > 0 ? (
                                <div className="space-y-4 sm:space-y-6">
                                    {reviews.map((review) => (
                                        <div key={review._id} className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-5 md:p-6">
                                            <div className="flex items-start justify-between mb-4 flex-col sm:flex-row gap-4 sm:gap-2">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                                                        {review.reviewer.avatar ? (
                                                            <img
                                                                src={review.reviewer.avatar}
                                                                alt={review.reviewer.name}
                                                                className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover"
                                                            />
                                                        ) : (
                                                            <FiUser className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                                        )}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <h4 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white">
                                                            {review.reviewer.name}
                                                        </h4>
                                                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                                            {formatDate(review.date)}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="flex flex-shrink-0">
                                                    {[...Array(5)].map((_, i) => (
                                                        <FiStar
                                                            key={i}
                                                            className={`w-4 h-4 sm:w-5 sm:h-5 ${i < review.rating
                                                                ? 'text-yellow-500 fill-current'
                                                                : 'text-gray-300 dark:text-gray-600'
                                                                }`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>

                                            <p className="text-xs sm:text-sm md:text-base text-gray-700 dark:text-gray-300 mb-3">
                                                {review.comment}
                                            </p>

                                            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                                                Project: <span className="font-medium">{review.gigTitle}</span>
                                            </p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-12 sm:py-16">
                                    <FiStar className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                                    <h3 className="text-base sm:text-lg md:text-xl font-medium text-gray-900 dark:text-white mb-2">
                                        No reviews yet
                                    </h3>
                                    <p className="text-xs sm:text-sm md:text-base text-gray-500 dark:text-gray-400">
                                        Reviews will appear here once work is completed.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </>
    )
}

export default ProfilePage